/**
 * Публичная документация ПО «Бай.Спорт» для сайта правообладателя
 * (в т.ч. для заявления в реестр отечественного ПО).
 */
import { LEGAL_OPERATOR } from "./legalDocuments";
import { TRIAL_DAYS } from "./trial";

export const PRODUCT_DOCS_UPDATED_AT = "8 октября 2026 г.";

export const PRODUCT_DOCS = {
  title: "Документация программного обеспечения «Бай.Спорт»",
  description:
    "Описание функциональных характеристик ПО «Бай.Спорт» и информация, необходимая для установки и эксплуатации.",
  updatedAt: PRODUCT_DOCS_UPDATED_AT,
  sections: [
    {
      heading: "1. Общие сведения",
      paragraphs: [
        `Полное наименование программного обеспечения: «Бай.Спорт» (альтернативные названия: BaySport, BaySport CRM).`,
        `Правообладатель: ${LEGAL_OPERATOR.legalName}, ИНН ${LEGAL_OPERATOR.inn}, ${LEGAL_OPERATOR.ogrnOrOgrnip}.`,
        `Официальный сайт: ${LEGAL_OPERATOR.siteUrl}. Контакт поддержки: ${LEGAL_OPERATOR.supportEmail}.`,
        `«Бай.Спорт» — облачное программное обеспечение (SaaS), предназначенное для автоматизации деятельности спортивных клубов, секций и тренеров: учёт клиентов, расписание, посещаемость, абонементы и оплаты, коммуникации и личные кабинеты участников.`,
      ],
    },
    {
      heading: "2. Назначение программного обеспечения",
      paragraphs: [
        "ПО используется спортивными организациями для ведения клиентской базы, организации тренировочного процесса, контроля посещаемости и финансовых операций, а также для предоставления участникам и родителям доступа к личному кабинету клуба.",
        "Программное обеспечение распространяется как интернет-сервис (SaaS): установка на рабочие станции пользователей не требуется. Доступ осуществляется через веб-браузер или устанавливаемое Progressive Web Application (PWA).",
      ],
    },
    {
      heading: "3. Функциональные характеристики",
      paragraphs: [
        "3.1. Мультитенантная архитектура — отдельный рабочий контур (кабинет) для каждого спортивного клуба.",
        "3.2. Ролевая модель доступа: владелец клуба, администратор, тренер, клиент (родитель / участник).",
        "3.3. Клиентская база (CRM): карточки участников, статусы, заметки, источники, семейные профили (родитель — дети).",
        "3.4. Управление группами: вид спорта, тренер, зал/площадка, лимит мест, уровень подготовки.",
        "3.5. Расписание занятий: повторяющиеся и разовые занятия, события, соревнования и приём заявок.",
        "3.6. Журнал посещаемости с отметкой явки в кабинетах администратора и тренера; статистика по участникам и группам.",
        "3.7. Финансы клуба: абонементы, разовые платежи, баланс, история оплат, напоминания об оплате.",
        "3.8. Онлайн-оплата услуг клуба при подключении платёжного провайдера (эквайринг).",
        "3.9. Коммуникации: push-уведомления (PWA), рассылки, опросы явки и анкеты.",
        "3.10. Клиентский портал: расписание, посещения, оплаты, информация о клубе и лента клуба.",
        "3.11. Кабинет тренера: расписание, журнал, работа с группами и опросами.",
        "3.12. Административный кабинет: настройки клуба, команда, финансы, управление модулями.",
        "3.13. Персонализация портала клуба (white-label): логотип, цвета, обложка, брендинг.",
        "3.14. Подписка на платформу по тарифам с лимитами по числу тренеров, участников и доступным модулям.",
        "3.15. Работа в виде веб-приложения и устанавливаемого PWA на мобильных устройствах.",
      ],
    },
    {
      heading: "4. Сведения, необходимые для установки программного обеспечения",
      paragraphs: [
        "4.1. Для пользователей (клубов, тренеров, клиентов) отдельная установка дистрибутива не требуется. ПО предоставляется как облачный сервис.",
        `4.2. Для начала работы владелец клуба регистрирует организацию на сайте ${LEGAL_OPERATOR.siteUrl} (раздел регистрации клуба).`,
        `4.3. После регистрации предоставляется демо-доступ на ${TRIAL_DAYS} дней с полным функционалом тарифа «Максимальный».`,
        "4.4. Минимальные требования к рабочему месту пользователя: современный браузер с поддержкой JavaScript (актуальные версии Google Chrome, Yandex Browser, Mozilla Firefox, Safari, Microsoft Edge); стабильный доступ в сеть Интернет; для push-уведомлений — поддержка Service Worker / PWA.",
        "4.5. Рекомендуемые устройства: персональный компьютер, ноутбук, планшет или смартфон. Для удобства мобильной работы приложение можно установить на устройство как PWA через меню браузера («Установить приложение» / «На экран „Домой“»).",
        "4.6. Развёртывание серверной части для конечного пользователя не выполняется: серверные компоненты размещаются и обслуживаются правообладателем.",
      ],
    },
    {
      heading: "5. Сведения, необходимые для эксплуатации программного обеспечения",
      paragraphs: [
        "5.1. Вход в систему выполняется по учётной записи (адрес электронной почты / логин и пароль) на страницах авторизации соответствующих порталов.",
        "5.2. Порталы пользователей:",
        "— административный кабинет владельца/администратора клуба: раздел /manage/app;",
        "— кабинет тренера: раздел /staff/app;",
        "— кабинет клиента (родителя/участника): раздел /app;",
        `— кабинет владельца на платформе: ${LEGAL_OPERATOR.siteUrl}/account.`,
        "5.3. Порядок первичной настройки клуба: указать данные организации и виды спорта → добавить залы/площадки → создать группы и назначить тренеров → настроить абонементы → пригласить участников по ссылке или QR-коду → вести журнал и оплаты.",
        "5.4. Управление доступом сотрудников осуществляется владельцем/администратором через раздел команды клуба (приглашение тренеров и администраторов).",
        "5.5. Тариф и оплата подписки на платформу управляются в кабинете владельца. Актуальные тарифы публикуются на сайте правообладателя в разделе «Тарифы».",
        `5.6. Техническая поддержка: ${LEGAL_OPERATOR.supportEmail}.`,
        "5.7. Резервное копирование данных, обновление версий ПО и обеспечение доступности сервиса выполняются правообладателем в рамках оказания SaaS-услуги.",
      ],
    },
    {
      heading: "6. Стоимость программного обеспечения",
      paragraphs: [
        "Стоимость формируется в соответствии с тарифной политикой правообладателя и зависит от выбранного тарифа подписки клуба.",
        "Базовые публичные тарифы (стоимость за один клуб в месяц, без учёта акций): «Стартовый» — 790 ₽; «Расширенный» — 1 490 ₽; «Максимальный» — 2 490 ₽.",
        `Актуальная информация о стоимости размещена на странице: ${LEGAL_OPERATOR.siteUrl}/#pricing.`,
      ],
    },
    {
      heading: "7. Ограничения и особые условия",
      paragraphs: [
        "7.1. Программное обеспечение не является медицинским изделием и не предназначено для постановки медицинских диагнозов.",
        "7.2. Специализированные функции для пользователей с ограничениями по слуху или зрению в текущей версии не заявлены.",
        "7.3. Активация, выпуск, распространение и управление лицензионными ключами не осуществляются: доступ предоставляется по учётной записи и подписке SaaS.",
      ],
    },
  ],
};
